import {
  FORMULA_FUNCTION_ARITY,
  FORMULA_FUNCTION_NAMES,
  FormulaBinaryOp,
  FormulaFunctionName,
  FormulaNode,
} from "../../types/recommendationEngine";

const FUNCTION_NAME_SET = new Set<string>(FORMULA_FUNCTION_NAMES);

// Includes both the ASCII hyphen and U+2212 "−" — the latter is what
// astToFormulaText and the editor's quick-insert button emit for subtraction.
const OP_CHARS = new Set(["+", "-", "−", "×", "÷", "*", "/"]);

type Token =
  | { type: "number"; value: number; pos: number }
  | { type: "ident"; value: string; pos: number }
  | { type: "op"; value: string; pos: number }
  | { type: "lparen"; pos: number }
  | { type: "rparen"; pos: number }
  | { type: "comma"; pos: number };

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "lparen", pos: i });
      i += 1;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "rparen", pos: i });
      i += 1;
      continue;
    }
    if (ch === ",") {
      tokens.push({ type: "comma", pos: i });
      i += 1;
      continue;
    }
    if (OP_CHARS.has(ch)) {
      tokens.push({ type: "op", value: ch, pos: i });
      i += 1;
      continue;
    }
    if (/[\d.]/.test(ch)) {
      const start = i;
      const raw = input
        .slice(i)
        .match(/^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/)?.[0];
      if (!raw) {
        throw new Error(`Invalid number at position ${start + 1}`);
      }
      i += raw.length;
      const value = Number(raw);
      if (!Number.isFinite(value)) {
        throw new Error(`Invalid number "${raw}" at position ${start + 1}`);
      }
      tokens.push({ type: "number", value, pos: start });
      continue;
    }
    if (/[a-zA-Z_]/.test(ch)) {
      const start = i;
      i += 1;
      while (i < input.length && /[a-zA-Z0-9_]/.test(input[i])) {
        i += 1;
      }
      tokens.push({ type: "ident", value: input.slice(start, i), pos: start });
      continue;
    }
    throw new Error(`Unexpected character "${ch}" at position ${i + 1}`);
  }
  return tokens;
}

function normalizeOp(op: string): FormulaBinaryOp {
  if (op === "+" || op === "−" || op === "-") {
    return op === "+" ? "+" : "-";
  }
  if (op === "×" || op === "*") {
    return "*";
  }
  if (op === "÷" || op === "/") {
    return "/";
  }
  throw new Error(`Unknown operator "${op}"`);
}

class Parser {
  private index = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly knownSignals: Set<string>,
  ) {}

  parse(): FormulaNode {
    const node = this.parseExpr();
    if (this.index < this.tokens.length) {
      const t = this.tokens[this.index];
      throw new Error(`Unexpected token at position ${t.pos + 1}`);
    }
    return node;
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private consume(): Token {
    const t = this.tokens[this.index];
    if (!t) {
      throw new Error("Unexpected end of formula");
    }
    this.index += 1;
    return t;
  }

  private parseExpr(): FormulaNode {
    let left = this.parseTerm();
    while (this.peek()?.type === "op") {
      const opToken = this.peek() as Token & { type: "op" };
      if (opToken.value !== "+" && opToken.value !== "-" && opToken.value !== "−") {
        break;
      }
      this.consume();
      const right = this.parseTerm();
      left = {
        type: "binary",
        op: normalizeOp(opToken.value),
        left,
        right,
      };
    }
    return left;
  }

  private parseTerm(): FormulaNode {
    let left = this.parseFactor();
    while (this.peek()?.type === "op") {
      const opToken = this.peek() as Token & { type: "op" };
      if (
        opToken.value !== "×" &&
        opToken.value !== "*" &&
        opToken.value !== "÷" &&
        opToken.value !== "/"
      ) {
        break;
      }
      this.consume();
      const right = this.parseFactor();
      left = {
        type: "binary",
        op: normalizeOp(opToken.value),
        left,
        right,
      };
    }
    return left;
  }

  private parseFactor(): FormulaNode {
    const t = this.peek();
    if (!t) {
      throw new Error("Unexpected end of formula");
    }
    if (t.type === "op" && (t.value === "-" || t.value === "−")) {
      this.consume();
      const inner = this.parseFactor();
      if (inner.type === "literal") {
        return { type: "literal", value: -inner.value };
      }
      return {
        type: "binary",
        op: "*",
        left: { type: "literal", value: -1 },
        right: inner,
      };
    }
    if (t.type === "op" && t.value === "+") {
      this.consume();
      return this.parseFactor();
    }
    if (t.type === "number") {
      this.consume();
      return { type: "literal", value: t.value };
    }
    if (t.type === "ident") {
      this.consume();
      if (this.peek()?.type === "lparen") {
        if (!FUNCTION_NAME_SET.has(t.value)) {
          throw new Error(
            `Unknown function "${t.value}" at position ${t.pos + 1}`,
          );
        }
        this.consume();
        const args: FormulaNode[] = [];
        if (this.peek()?.type !== "rparen") {
          args.push(this.parseExpr());
          while (this.peek()?.type === "comma") {
            this.consume();
            args.push(this.parseExpr());
          }
        }
        const closing = this.consume();
        if (closing.type !== "rparen") {
          throw new Error(`Expected ")" near position ${closing.pos + 1}`);
        }
        const fnName = t.value as FormulaFunctionName;
        const arity = FORMULA_FUNCTION_ARITY[fnName];
        if (args.length < arity.min || args.length > arity.max) {
          const expected =
            arity.min === arity.max
              ? `${arity.min}`
              : `at least ${arity.min}`;
          throw new Error(
            `Function "${fnName}" expects ${expected} argument${
              arity.min === 1 && arity.max === 1 ? "" : "s"
            } (got ${args.length}) at position ${t.pos + 1}`,
          );
        }
        return { type: "function", name: fnName, args };
      }
      if (!this.knownSignals.has(t.value)) {
        throw new Error(`Unknown signal "${t.value}" at position ${t.pos + 1}`);
      }
      return { type: "signal", signalId: t.value };
    }
    if (t.type === "lparen") {
      this.consume();
      const inner = this.parseExpr();
      const closing = this.consume();
      if (closing.type !== "rparen") {
        throw new Error(`Expected ")" near position ${closing.pos + 1}`);
      }
      return inner;
    }
    throw new Error(`Unexpected token at position ${t.pos + 1}`);
  }
}

/** Human-editable formula text (uses ×, ÷, nested parens). */
export function astToFormulaText(node: FormulaNode): string {
  if (node.type === "literal") {
    return String(node.value);
  }
  if (node.type === "signal") {
    return node.signalId;
  }
  if (node.type === "function") {
    const args = node.args.map(astToFormulaText).join(", ");
    return `${node.name}(${args})`;
  }
  if (
    node.type === "binary" &&
    node.op === "*" &&
    node.left.type === "literal" &&
    node.right.type === "signal"
  ) {
    return `${node.left.value} × ${node.right.signalId}`;
  }
  if (
    node.type === "binary" &&
    node.op === "*" &&
    node.right.type === "literal" &&
    node.left.type === "signal"
  ) {
    return `${node.right.value} × ${node.left.signalId}`;
  }
  const l = astToFormulaText(node.left);
  const r = astToFormulaText(node.right);
  const sym = { "+": "+", "-": "−", "*": "×", "/": "÷" }[node.op];
  return `(${l} ${sym} ${r})`;
}

export function parseFormulaText(
  text: string,
  knownSignalIds: string[],
): { node: FormulaNode | null; error: string | null } {
  const trimmed = text.trim();
  if (!trimmed) {
    return { node: { type: "literal", value: 0 }, error: null };
  }
  try {
    const tokens = tokenize(trimmed);
    const parser = new Parser(tokens, new Set(knownSignalIds));
    return { node: parser.parse(), error: null };
  } catch (e) {
    return {
      node: null,
      error: e instanceof Error ? e.message : "Invalid formula",
    };
  }
}

export type FormulaTextSegment =
  | { type: "text"; value: string }
  | { type: "signal"; signalId: string };

function isSignalBoundary(text: string, start: number, idLen: number): boolean {
  const beforeOk =
    start === 0 || !/[a-zA-Z0-9_]/.test(text[start - 1] ?? "");
  const afterOk =
    start + idLen >= text.length ||
    !/[a-zA-Z0-9_]/.test(text[start + idLen] ?? "");
  return beforeOk && afterOk;
}

/** Split display text into editable text runs and locked signal tokens. */
export function formulaTextToSegments(
  text: string,
  signalIds: string[],
): FormulaTextSegment[] {
  const sortedIds = [...signalIds].sort((a, b) => b.length - a.length);
  const segments: FormulaTextSegment[] = [];
  let i = 0;

  while (i < text.length) {
    let matched = false;
    for (const id of sortedIds) {
      if (text.slice(i, i + id.length) === id && isSignalBoundary(text, i, id.length)) {
        segments.push({ type: "signal", signalId: id });
        i += id.length;
        matched = true;
        break;
      }
    }
    if (matched) {
      continue;
    }

    let j = i + 1;
    while (j < text.length) {
      let foundSignal = false;
      for (const id of sortedIds) {
        if (
          text.slice(j, j + id.length) === id &&
          isSignalBoundary(text, j, id.length)
        ) {
          foundSignal = true;
          break;
        }
      }
      if (foundSignal) {
        break;
      }
      j += 1;
    }
    segments.push({ type: "text", value: text.slice(i, j) });
    i = j;
  }

  if (segments.length === 0) {
    segments.push({ type: "text", value: "" });
  }
  return segments;
}

export function segmentsToFormulaText(segments: FormulaTextSegment[]): string {
  return segments
    .map((s) => (s.type === "text" ? s.value : s.signalId))
    .join("");
}

/** Strip characters that could form manual signal names — signals are insert-only. */
export function sanitizeEditableText(value: string): string {
  return value.replace(/[a-zA-Z_]/g, "");
}

export function mergeAdjacentTextSegments(
  segments: FormulaTextSegment[],
): FormulaTextSegment[] {
  const merged: FormulaTextSegment[] = [];
  segments.forEach((seg) => {
    if (seg.type === "text" && merged.at(-1)?.type === "text") {
      const prev = merged[merged.length - 1] as Extract<
        FormulaTextSegment,
        { type: "text" }
      >;
      prev.value += seg.value;
    } else {
      merged.push({ ...seg });
    }
  });
  if (merged.length === 0) {
    merged.push({ type: "text", value: "" });
  }
  return merged;
}
